/**
 * Subcontracting / B2B Booking Page - Deep-link enabled
 * Routes like /alltooovott/construction open directly to booking with preselected B2B subcategory
 */
import React from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import BookingForm from '@/components/BookingForm';
import { GlobalNav } from '@/components/GlobalNav';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { 
  Building2, Briefcase, Users, HardHat, Wrench, 
  Factory, Truck, Paintbrush, Plug, CircuitBoard,
  CheckCircle, Shield, ArrowLeft, Handshake
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Subcategory slug to ID mapping
const SUBCATEGORY_SLUGS = {
  'construction': 'construction',
  'ehitus': 'construction',
  'rakentaminen': 'construction',
  'electrical': 'electrical_work',
  'electrical_work': 'electrical_work',
  'elektritööd': 'electrical_work',
  'plumbing': 'plumbing',
  'torutööd': 'plumbing',
  'hvac': 'hvac',
  'küte-ventilatsioon': 'hvac',
  'painting': 'painting',
  'värvimine': 'painting',
  'welding': 'welding',
  'keevitus': 'welding',
  'manufacturing': 'manufacturing',
  'tootmine': 'manufacturing',
  'logistics': 'logistics',
  'logistika': 'logistics',
  'it-services': 'it_services',
  'it_services': 'it_services',
  'it-teenused': 'it_services',
  'cleaning-services': 'cleaning_services',
  'cleaning_services': 'cleaning_services',
  'puhastusteenused': 'cleaning_services'
};

// Subcategory display info
const SUBCATEGORY_INFO = {
  'construction': {
    icon: HardHat,
    label: { en: 'Construction Work', et: 'Ehitustööd', fi: 'Rakennustyöt', nb: 'Byggearbeid', ru: 'Строительные работы', sv: 'Byggnadsarbeten' },
    desc: { en: 'General construction and building projects', et: 'Üldised ehitus- ja hoonestusprojektid', fi: 'Yleiset rakennus- ja rakennusprojektit', nb: 'General construction and building projects', ru: 'General construction and building projects', sv: 'General construction and building projects' }
  },
  'electrical_work': {
    icon: Plug,
    label: { en: 'Electrical Work', et: 'Elektritööd', fi: 'Sähkötyöt', nb: 'Elektrikerarbeid', ru: 'Электромонтаж', sv: 'Elarbeten' },
    desc: { en: 'Electrical installations and maintenance', et: 'Elektripaigaldused ja hooldus', fi: 'Sähköasennukset ja huolto', nb: 'Electrical installations and maintenance', ru: 'Electrical installations and maintenance', sv: 'Electrical installations and maintenance' }
  },
  'plumbing': {
    icon: Wrench,
    label: { en: 'Plumbing', et: 'Torutööd', fi: 'Putkityöt', nb: 'Rørlegger', ru: 'Сантехника', sv: 'Rörmokeri' },
    desc: { en: 'Plumbing installations and repairs', et: 'Torupaigaldused ja remondid', fi: 'Putkiasennukset ja korjaukset', nb: 'Plumbing installations and repairs', ru: 'Plumbing installations and repairs', sv: 'Plumbing installations and repairs' }
  },
  'hvac': {
    icon: CircuitBoard,
    label: { en: 'HVAC', et: 'Küte ja ventilatsioon', fi: 'LVI-työt', nb: 'VVS / Ventilasjon', ru: 'Отопление и вентиляция', sv: 'VVS' },
    desc: { en: 'Heating, ventilation, air conditioning', et: 'Küte, ventilatsioon, kliimaseadmed', fi: 'Lämmitys, ilmanvaihto, ilmastointi', nb: 'Heating, ventilation, air conditioning', ru: 'Heating, ventilation, air conditioning', sv: 'Heating, ventilation, air conditioning' }
  },
  'painting': {
    icon: Paintbrush,
    label: { en: 'Painting & Finishing', et: 'Värvimine ja viimistlus', fi: 'Maalaus ja viimeistely', nb: 'Maling og finish', ru: 'Покраска и отделка', sv: 'Måleri & ytbehandling' },
    desc: { en: 'Interior and exterior painting', et: 'Sise- ja välisviimistlus', fi: 'Sisä- ja ulkomaalaus', nb: 'Interior and exterior painting', ru: 'Interior and exterior painting', sv: 'Interior and exterior painting' }
  },
  'welding': {
    icon: Factory,
    label: { en: 'Welding & Metalwork', et: 'Keevitus ja metallitööd', fi: 'Hitsaus ja metallityöt', nb: 'Sveising og metallarbeid', ru: 'Сварка и металлоработы', sv: 'Svetsning & metallarbete' },
    desc: { en: 'Industrial welding and metal fabrication', et: 'Tööstuslik keevitus ja metallitöötlus', fi: 'Teollinen hitsaus ja metallin valmistus', nb: 'Industrial welding and metal fabrication', ru: 'Industrial welding and metal fabrication', sv: 'Industrial welding and metal fabrication' }
  },
  'manufacturing': {
    icon: Factory,
    label: { en: 'Manufacturing', et: 'Tootmine', fi: 'Valmistus', nb: 'Produksjon', ru: 'Производство', sv: 'Tillverkning' },
    desc: { en: 'Production and assembly services', et: 'Tootmis- ja montaažiteenused', fi: 'Tuotanto- ja kokoonpanopalvelut', nb: 'Production and assembly services', ru: 'Production and assembly services', sv: 'Production and assembly services' }
  },
  'logistics': {
    icon: Truck,
    label: { en: 'Logistics', et: 'Logistika', fi: 'Logistiikka', nb: 'Logistikk', ru: 'Логистика', sv: 'Logistik' },
    desc: { en: 'Transport and warehouse services', et: 'Transpordi- ja laoteenused', fi: 'Kuljetus- ja varastopalvelut', nb: 'Transport and warehouse services', ru: 'Transport and warehouse services', sv: 'Transport and warehouse services' }
  },
  'it_services': {
    icon: CircuitBoard,
    label: { en: 'IT Services', et: 'IT-teenused', fi: 'IT-palvelut', nb: 'IT-tjenester', ru: 'IT-услуги', sv: 'IT-tjänster' },
    desc: { en: 'Software development and IT support', et: 'Tarkvaraarendus ja IT-tugi', fi: 'Ohjelmistokehitys ja IT-tuki', nb: 'Software development and IT support', ru: 'Software development and IT support', sv: 'Software development and IT support' }
  },
  'cleaning_services': {
    icon: Building2,
    label: { en: 'Cleaning Services', et: 'Puhastusteenused', fi: 'Siivouspalvelut', nb: 'Renholdstjenester', ru: 'Клининг', sv: 'Städtjänster' },
    desc: { en: 'Commercial and industrial cleaning', et: 'Äri- ja tööstuspuhastus', fi: 'Liike- ja teollisuussiivous', nb: 'Commercial and industrial cleaning', ru: 'Commercial and industrial cleaning', sv: 'Commercial and industrial cleaning' }
  }
};

const SubcontractingBookingPage = () => {
  const { subcategory: subcategorySlug } = useParams();
  const [searchParams] = useSearchParams();
  const { t, language } = useLanguage();
  
  // Resolve subcategory from slug or URL param
  const subcategoryId = SUBCATEGORY_SLUGS[subcategorySlug] || searchParams.get('subcategory') || null;
  const subcategoryInfo = subcategoryId ? SUBCATEGORY_INFO[subcategoryId] : null;
  const Icon = subcategoryInfo?.icon || Building2;

  // Labels for the page
  const labels = {
    en: {
      title: subcategoryInfo ? subcategoryInfo.label.en : 'Subcontracting',
      subtitle: subcategoryInfo ? subcategoryInfo.desc.en : 'Find skilled subcontractors for your project',
      backToB2B: 'Back to B2B Services',
      features: ['Verified companies', 'Project-based work', 'Quality guarantee', 'Flexible terms']
    },
    et: {
      title: subcategoryInfo ? subcategoryInfo.label.et : 'Alltöövõtt',
      subtitle: subcategoryInfo ? subcategoryInfo.desc.et : 'Leia kvalifitseeritud alltöövõtjad oma projektile',
      backToB2B: 'Tagasi B2B teenuste juurde',
      features: ['Kontrollitud ettevõtted', 'Projektipõhine töö', 'Kvaliteedigarantii', 'Paindlikud tingimused']
    },
    fi: {
      title: subcategoryInfo ? subcategoryInfo.label.fi : 'Alihankinta',
      subtitle: subcategoryInfo ? subcategoryInfo.desc.fi : 'Löydä pätevät alihankkijat projektiisi',
      backToB2B: 'Takaisin B2B-palveluihin',
      features: ['Vahvistetut yritykset', 'Projektipohjainen työ', 'Laatutakuu', 'Joustavat ehdot']
    },
    nb: {
      title: subcategoryInfo ? (subcategoryInfo.label.nb || subcategoryInfo.label.en) : 'Underleverandører',
      subtitle: subcategoryInfo ? (subcategoryInfo.desc.nb || subcategoryInfo.desc.en) : 'Finn dyktige underleverandører til prosjektet ditt',
      backToB2B: 'Tilbake til B2B-tjenester',
      features: ['Verifiserte selskaper', 'Prosjektbasert arbeid', 'Kvalitetsgaranti', 'Fleksible vilkår']
    },
    ru: {
      title: subcategoryInfo ? (subcategoryInfo.label.ru || subcategoryInfo.label.en) : 'Субподряд',
      subtitle: subcategoryInfo ? (subcategoryInfo.desc.ru || subcategoryInfo.desc.en) : 'Найдите квалифицированных субподрядчиков для вашего проекта',
      backToB2B: 'Назад к B2B-услугам',
      features: ['Проверенные компании', 'Работа по проектам', 'Гарантия качества', 'Гибкие условия']
    },
    sv: {
      title: subcategoryInfo ? (subcategoryInfo.label.sv || subcategoryInfo.label.en) : 'Hitta underleverantörer',
      subtitle: subcategoryInfo ? (subcategoryInfo.desc.sv || subcategoryInfo.desc.en) : 'Hitta kvalificerade underleverantörer för ditt projekt',
      backToB2B: 'Tillbaka till B2B-tjänster',
      features: ['Verifierade företag', 'Projektbaserat arbete', 'Kvalitetsgaranti', 'Flexibla villkor']
    }
  };

  const l = labels[language] || labels.en;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-slate-50">
      <GlobalNav />
      
      <main className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        {/* Back link */}
        <Link to="/alltooovott" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="h-4 w-4" />
          {l.backToB2B}
        </Link>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left: Info Section */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge className="mb-4 bg-indigo-100 text-indigo-700">
                <Icon className="h-3.5 w-3.5 mr-1" />
                {language === 'et' ? 'B2B teenused' : language === 'fi' ? 'B2B-palvelut' : language === 'nb' ? 'B2B-tjenester' : language === 'ru' ? 'B2B-услуги' : language === 'sv' ? 'B2B-tjänster' : 'B2B Services'}
              </Badge>
              
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3" data-testid="subcontracting-booking-title">
                {l.title}
              </h1>
              
              <p className="text-lg text-slate-600 max-w-lg" data-testid="subcontracting-booking-subtitle">
                {l.subtitle}
              </p>
            </motion.div>

            {/* Features */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap gap-3"
            >
              {l.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-white rounded-full shadow-sm border text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="font-medium">{feature}</span>
                </div>
              ))}
            </motion.div>

            {/* Trust Banner */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Card className="bg-gradient-to-r from-indigo-600 to-indigo-800 text-white border-0">
                <CardContent className="py-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-white/20 rounded-xl">
                      <Handshake className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">
                        {language === 'et' ? 'Äripartnerid' : language === 'fi' ? 'Liikekumppanit' : language === 'nb' ? 'Forretningspartnere' : language === 'ru' ? 'Бизнес-партнёры' : 'Business Partners'}
                      </h3>
                      <p className="text-indigo-100 text-sm">
                        {language === 'et' ? 'Ühenda kontrollitud B2B teenusepakkujatega' : language === 'fi' ? 'Yhdistä vahvistettuihin B2B-palveluntarjoajiin' : language === 'nb' ? 'Koble til verifiserte B2B-tjenesteleverandører' : language === 'ru' ? 'Свяжитесь с проверенными поставщиками B2B-услуг' : language === 'sv' ? 'Anslut till verifierade B2B-tjänsteleverantörer' : 'Connect with verified B2B service providers'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Right: Booking Form with preselection */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:sticky lg:top-24"
          >
            <BookingForm 
              defaultCategory="subcontracting"
              defaultSubcategory={subcategoryId}
            />
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default SubcontractingBookingPage;
